/**
 * KinsCircle Intelligence Layer V1 — Review Fix Modal
 *
 * Command Center confirmation surface for a governed operational repair. Renders
 * the issue, the affected member(s), the exact field-level "before → after"
 * preview, the risk level, the "create backup first" reminder, a Cancel
 * button, and an Apply Fix button.
 *
 * V1 contract:
 *   - Pure UI surface — never calls supabase directly. The Apply button
 *     dispatches to `applyRepair()` from `@/lib/intelligence/repairActions`,
 *     which re-runs every safety check live before writing.
 *   - Render returns null when not open (so a closed modal has zero cost).
 *   - The owner / admin gate is enforced TWICE: once when this modal
 *     mounts (the parent only renders it when canRunIntelligenceDiagnostics
 *     is true) AND once inside applyRepair (defense in depth).
 *   - When `proposal.reversible === false`, the modal shows an extra
 *     amber "irreversible" banner and the Apply button copy changes to
 *     "Apply Fix (cannot be undone)".
 *   - Always shows the "Export Family Tree Backup first" reminder (links
 *     to the existing tool in src/lib/familyBackupExport.ts via the
 *     /account page rather than running it here — keeps this modal
 *     side-effect-free).
 *
 * Honors STABILITY.md §1, §3, §7.7 (backup reminder), §9 (intelligence V1
 * report-only base contract), §9.10 (owner-approved repair extension).
 */

import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Wrench,
  X,
  ArrowRight,
  Database,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import type { RepairProposal } from '@/lib/intelligence/repairActions';

interface Props {
  open: boolean;
  proposal: RepairProposal | null;
  applying: boolean;
  onCancel: () => void;
  onApply: () => void;
}

const formatBeforeAfter = (value: unknown): string => {
  if (value === null) return '—';
  if (value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const ReviewFixModal: React.FC<Props> = ({
  open,
  proposal,
  applying,
  onCancel,
  onApply,
}) => {
  const [acknowledgedBackup, setAcknowledgedBackup] = useState(false);

  // Reset the backup acknowledgement whenever a different proposal opens
  // so a previous "I exported a backup" tick doesn't leak across issues.
  React.useEffect(() => {
    setAcknowledgedBackup(false);
  }, [proposal?.issueId, open]);

  const irreversible = useMemo(
    () => !!proposal && proposal.reversible === false,
    [proposal],
  );

  const applyDisabled = applying || !proposal || (irreversible && !acknowledgedBackup);

  if (!open || !proposal) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !applying) onCancel(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wrench className="w-5 h-5 text-fuchsia-600" />
            Review Fix
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 text-[10px] font-semibold">
              <ShieldCheck className="w-3 h-3" />
              Command Center only
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold uppercase">
              Risk: {proposal.riskLevel}
            </span>
          </DialogTitle>
          <DialogDescription className="mt-1 text-slate-600">
            Review every change this repair will make before you apply it.
            Nothing is written until you click <strong>Apply Fix</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Issue summary */}
          <section className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Issue detected
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {proposal.issueTitle}
            </div>
            {proposal.affectedNames.length > 0 && (
              <div className="mt-1 text-xs text-slate-500">
                Affected:{' '}
                <span className="text-slate-700">
                  {proposal.affectedNames.join(', ')}
                </span>
              </div>
            )}
          </section>

          {/* Proposed change */}
          <section className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Proposed change
            </div>
            <div className="mt-1 text-sm text-slate-700">
              {proposal.description}
            </div>
          </section>

          {/* Field-level diff */}
          <section className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <Database className="w-3.5 h-3.5" />
              Exact fields that will change
            </div>
            <ul className="mt-2 divide-y divide-slate-100">
              {proposal.fieldsThatWillChange.map((change, idx) => (
                <li key={idx} className="py-2 flex flex-col sm:flex-row sm:items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500">{change.memberName}</div>
                    <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                      {change.field}
                    </code>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded border ${
                        change.op === 'delete'
                          ? 'border-rose-200 bg-rose-50 text-rose-700'
                          : 'border-slate-200 bg-slate-50 text-slate-700'
                      }`}
                    >
                      {formatBeforeAfter(change.before)}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded border ${
                        change.op === 'delete'
                          ? 'border-rose-300 bg-rose-100 text-rose-800 font-semibold'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold'
                      }`}
                    >
                      {formatBeforeAfter(change.after)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Backup reminder */}
          <section className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                <div className="font-semibold">Create a backup first</div>
                <div className="mt-1">
                  Export your family tree from the{' '}
                  <Link
                    to="/settings"
                    className="underline font-semibold hover:text-amber-700"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Account page
                  </Link>{' '}
                  before applying any structural repair. Your existing
                  family graph rows are not modified by V1, but a backup
                  protects you from any future regression.
                </div>
              </div>
            </div>
          </section>

          {/* Irreversibility warning */}
          {irreversible && (
            <section className="rounded-lg border border-rose-200 bg-rose-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-xs text-rose-900">
                  <div className="font-semibold">
                    This repair cannot be undone
                  </div>
                  <div className="mt-1">
                    The change shown above removes a row that has no
                    memories, photos, comments, or claimed account — but
                    once removed it is permanent. Please export a backup
                    above before continuing.
                  </div>
                  <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-rose-300"
                      checked={acknowledgedBackup}
                      onChange={(e) => setAcknowledgedBackup(e.target.checked)}
                      disabled={applying}
                    />
                    <span className="font-semibold">
                      I have exported a family tree backup and accept that
                      this change cannot be undone.
                    </span>
                  </label>
                </div>
              </div>
            </section>
          )}

          {/* Reversibility footer */}
          {!irreversible && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                This change is reversible. You can revert it later via the
                "Undo last repair" button on the Tree Intelligence card.
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={applying}
            className="rounded-full"
          >
            <X className="w-4 h-4 mr-1.5" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onApply}
            disabled={applyDisabled}
            className={`rounded-full gap-1.5 text-white ${
              irreversible
                ? 'bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600'
                : 'bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600'
            }`}
          >
            {applying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Wrench className="w-4 h-4" />
            )}
            {irreversible ? 'Apply Fix (cannot be undone)' : 'Apply Fix'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewFixModal;
